import { useContext, useState, useEffect, useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";
import DatePicker from "react-datepicker";

import MessageModal from "../messageModal";
import ErrorModal from "../errorModal";
import Modal from "../modal";
import ToggleButton from "../toggleButton";

import fire from "../../assets/fire.png";

import { isDate, isDateInRange, getCompleteDate } from "../../utils/util";

import { W3Context } from "../../services/context";
import getContracts from "../../services/contract/getContracts";
import { sendStartAuctionEvent } from "../../services/gtm";

import { DEFAULT_ERROR_MESSAGE } from "../../utils/util";

import "./auctionSettingsModalStyles.css";

const { nftsContract, auctionsContract, geosContract } = getContracts();

const QUICKSWAP_URL = `https://quickswap.exchange/#/swap?outputCurrency=${geosContract.address}`;

const { REACT_APP_PROMOTION_FEE = "1000", REACT_APP_CURRENCY_TOKEN } =
  process.env;

const MAXIMUM_DAYS_TO_START_DATE = 30;
const MINIMUM_DAYS_TO_END_DATE_FROM_START_DATE = 1;
const MAXIMUM_DAYS_TO_END_DATE_FROM_START_DATE = 7;

const DATE_FORMAT = "MMMM d, yyyy h:mm aa";

// https://stackoverflow.com/a/10789415
const COEFFICIENT = 1000 * 60 * 30;
let currentDate = new Date();
currentDate = new Date(
  Math.round(currentDate.getTime() / COEFFICIENT) * COEFFICIENT
);

const minimumStartDate = new Date(currentDate.getTime());
minimumStartDate.setSeconds(0, 0);
const maximumStartDate = new Date(currentDate.getTime());
maximumStartDate.setDate(
  maximumStartDate.getDate() + MAXIMUM_DAYS_TO_START_DATE
);
maximumStartDate.setSeconds(0, 0);

const initEndDate = new Date(currentDate.getTime());
const initMinEndDate = new Date(currentDate.getTime());
const initMaxEndDate = new Date(currentDate.getTime());

const AuctionSettingsModal = ({
  displayModal,
  setDisplayModal,
  tokenId,
  setUpdateData,
  GAS_FEE,
}) => {
  const { active, account, library } = useContext(W3Context);

  const [handlerFunction, setHandlerFunction] = useState(undefined);

  const [displayMessageModal, setDisplayMessageModal] = useState(false);
  const [displayErrorModal, setDisplayErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE);

  const [messageModalBody, setMessageModalBody] = useState("");
  const [messageModalButtonText, setMessageModalButtonText] = useState("");
  const [messageModalTitle, setMessageModalTitle] = useState("");
  const [messageModalFooterText, setMessageModalFooterText] = useState("");

  const [isHottest, setIsHottest] = useState(false);
  const [promotionFee, setPromotionFee] = useState(REACT_APP_PROMOTION_FEE);

  const [price, setPrice] = useState(0);
  const [hasPriceError, setHasPriceError] = useState(true);

  const [startDateTime, setStartDateTime] = useState(minimumStartDate);
  const [endDateTime, setEndDateTime] = useState(
    new Date(
      initEndDate.setDate(
        minimumStartDate.getDate() + MINIMUM_DAYS_TO_END_DATE_FROM_START_DATE
      )
    )
  );
  const [minimumEndDate, setMinimumEndDate] = useState(
    new Date(
      initMinEndDate.setDate(
        minimumStartDate.getDate() + MINIMUM_DAYS_TO_END_DATE_FROM_START_DATE
      )
    )
  );
  const [maximumEndDate, setMaximumEndDate] = useState(
    new Date(
      initMaxEndDate.setDate(
        minimumStartDate.getDate() + MAXIMUM_DAYS_TO_END_DATE_FROM_START_DATE
      )
    )
  );

  const getFeePrice = useCallback(async () => {
    try {
      if (active) {
        const auctionsContractInstance = new library.eth.Contract(
          auctionsContract.abi,
          auctionsContract.address
        );

        // TODO: change fee method
        const fee = await auctionsContractInstance.methods.hottestFee().call();

        setPromotionFee(library.utils.fromWei(fee, "ether"));
      }
    } catch (err) {
      console.log(err);
    }
  }, [library, active]);

  useEffect(() => {
    getFeePrice();
  }, [getFeePrice]);

  // target="_blank" vulnerability
  // https://medium.com/@jitbit/target-blank-the-most-underestimated-vulnerability-ever-96e328301f4c
  const redirectToQuickSwap = () => {
    const newWindow = window.open(
      QUICKSWAP_URL,
      "_blank",
      "noopener,noreferrer"
    );
    if (newWindow) newWindow.opener = null;
  };

  const setMessageModalAttributes = (
    title,
    body,
    buttonText,
    handlerFunction = false,
    footerText = ""
  ) => {
    setMessageModalTitle(title);
    setMessageModalBody(body);
    setMessageModalButtonText(buttonText);

    setHandlerFunction(handlerFunction);

    setMessageModalFooterText(footerText);

    setDisplayMessageModal(true);
  };

  const onChangePrice = (event) => {
    const value = event?.target?.value;
    if (!value || isNaN(value) || parseInt(value) <= 0) {
      setHasPriceError(true);
      return;
    }
    setHasPriceError(false);
    setPrice(parseInt(value));
  };

  const onKeyPressPrice = (e) => !/[0-9]/.test(e.key) && e.preventDefault();

  const addDaysToDate = (date, days) => {
    if (!isDate(date)) {
      return null;
    }

    const returnDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate() + days,
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    );

    return returnDate;
  };

  const onChangeStartDateTime = (date) => {
    setStartDateTime(date);
    setMinimumEndDate(
      addDaysToDate(date, MINIMUM_DAYS_TO_END_DATE_FROM_START_DATE)
    );
    setMaximumEndDate(
      addDaysToDate(date, MAXIMUM_DAYS_TO_END_DATE_FROM_START_DATE)
    );
  };

  const filterTimeStartDate = (date) => {
    const selectedDate = new Date(date);
    return isDateInRange(selectedDate, minimumStartDate, maximumStartDate);
  };

  const filterTimeEndDate = (date) => {
    const selectedDate = new Date(date);
    return isDateInRange(selectedDate, minimumEndDate, maximumEndDate);
  };

  const startAuction = async ({
    auctionType,
    tokenId,
    timeToStart,
    startPrice,
    duration,
    currencyToken,
    discountRate,
    minPercentage,
    isHottest,
  }) => {
    const auctionsContractInstance = new library.eth.Contract(
      auctionsContract.abi,
      auctionsContract.address
    );

    const myAddress = library.utils.toChecksumAddress(account);

    await auctionsContractInstance.methods
      .startAuction(
        auctionType,
        tokenId,
        timeToStart,
        startPrice,
        duration,
        currencyToken,
        discountRate,
        minPercentage,
        isHottest
      )
      .send({
        from: myAddress,
        gasPrice: library.utils.toWei(GAS_FEE, "gwei"),
      })
      .then((res) => {
        setUpdateData(res);

        setMessageModalAttributes(
          "Your NFT has been successfully put up for auction",
          "You'll be notified when someone places a bid",
          "Awesome",
          false,
          "It may take a some time for your auction to appear on My Auctions. Please, refresh the page if the auction does not appear."
        );
        setDisplayModal(false);

        sendStartAuctionEvent();
        return;
      })
      .catch((err) => {
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
        setDisplayErrorModal(true);
        console.log(err);
      });
  };

  const approveContractForNFT = async (auction) => {
    const nftContractInstance = new library.eth.Contract(
      nftsContract.abi,
      nftsContract.address
    );

    const myAddress = library.utils.toChecksumAddress(account);
    const auctionsContractAddress = library.utils.toChecksumAddress(
      auctionsContract.address
    );

    try {
      const isApproved = await nftContractInstance.methods
        .isApprovedForAll(myAddress, auctionsContractAddress)
        .call();

      if (!isApproved) {
        await nftContractInstance.methods
          .setApprovalForAll(auctionsContractAddress, true)
          .send({
            from: myAddress,
            gasPrice: library.utils.toWei(GAS_FEE, "gwei"),
          })
          .then((res) => {
            startAuction(auction);
          })
          .catch((err) => {
            setErrorMessage(DEFAULT_ERROR_MESSAGE);
            setDisplayErrorModal(true);
            console.log(err);
          });
        return;
      }

      startAuction(auction);
    } catch (err) {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
      setDisplayErrorModal(true);
      console.log(err);
    }
  };

  const buildAuctionData = async () => {
    const now = new Date();
    const secondsStartDate = Math.round(startDateTime.getTime() / 1000);
    const secondsNowDate = Math.round(now.getTime() / 1000);
    const secondsEndDate = Math.round(endDateTime.getTime() / 1000);
    // timeToStart has to be in seconds
    let timeToStart = 0;

    if (startDateTime.getTime() > now.getTime()) {
      timeToStart = secondsStartDate - secondsNowDate;
    }

    // duration has to be in seconds
    const duration = secondsEndDate - secondsStartDate;

    const auction = {
      auctionType: 1,
      tokenId,
      timeToStart,
      startPrice: library.utils.toWei(String(price), "ether"),
      duration,
      currencyToken: REACT_APP_CURRENCY_TOKEN,
      discountRate: 0,
      minPercentage: 0,
      isHottest,
    };

    await approveContractForNFT(auction);
  };

  const approveContractForGeos = async () => {
    const geosContractInstance = new library.eth.Contract(
      geosContract.abi,
      geosContract.address
    );

    const myAddress = library.utils.toChecksumAddress(account);
    const auctionsContractAddress = library.utils.toChecksumAddress(
      auctionsContract.address
    );

    try {
      const balance = await geosContractInstance.methods
        .balanceOf(myAddress)
        .call();

      const availableGeos = parseFloat(library.utils.fromWei(balance, "ether"));
      const fee = parseFloat(library.utils.fromWei(promotionFee, "ether"));

      if (fee > availableGeos) {
        setMessageModalAttributes(
          "You don't have enough GEO$",
          "Buy more and come back to promote your auction",
          "Go to QuickSwap!",
          true
        );
        return;
      }

      const approved = await geosContractInstance.methods
        .allowance(myAddress, auctionsContractAddress)
        .call();

      const approvedGeos = parseFloat(library.utils.fromWei(approved, "ether"));
      const amount = library.utils.toWei(promotionFee, "ether");

      if (amount > approvedGeos) {
        await geosContractInstance.methods
          .approve(auctionsContractAddress, amount)
          .send({
            from: myAddress,
            gasPrice: library.utils.toWei(GAS_FEE, "gwei"),
          })
          .then((res) => {
            buildAuctionData();
          })
          .catch((err) => {
            setErrorMessage(DEFAULT_ERROR_MESSAGE);
            setDisplayErrorModal(true);
            console.log(err);
          });
        return;
      }

      buildAuctionData();
    } catch (err) {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
      setDisplayErrorModal(true);
      console.log(err);
    }
  };

  const onClickConfirm = async () => {
    if (hasPriceError || price <= 0) {
      setMessageModalAttributes("Error in price", "The price is invalid", "OK");
      return;
    }

    if (startDateTime.getTime() >= endDateTime.getTime()) {
      setMessageModalAttributes(
        "Error in start time",
        <>The start time cannot be after the end time</>,
        "OK"
      );
      return;
    }

    if (!isDateInRange(startDateTime, minimumStartDate, maximumStartDate)) {
      setMessageModalAttributes(
        "Error in start time",
        <>
          The start time should be between <br />
          {getCompleteDate(minimumStartDate)} <br /> and <br />
          {getCompleteDate(maximumStartDate)}
        </>,
        "OK"
      );
      return;
    }

    if (!isDateInRange(endDateTime, minimumEndDate, maximumEndDate)) {
      setMessageModalAttributes(
        "Error in end time",
        <>
          The end time should be between <br />
          {getCompleteDate(minimumEndDate)} <br /> and <br />
          {getCompleteDate(maximumEndDate)}
        </>,
        "OK"
      );
      return;
    }

    if (isHottest) {
      approveContractForGeos();
      return;
    }

    buildAuctionData();
  };

  const getBodyContent = () => (
    <Container className="auction-settings-modal">
      <Row className="justify-content-center">
        <Col xs={12} lg={3}>
          <Row className="justify-content-center mt-1">
            <span className="title">Starting value</span>
          </Row>
          <Row className="justify-content-center">
            <Col xs={12}>
              <div className="background-input">
                <input
                  type="number"
                  min={0}
                  placeholder="999 GEO$"
                  onKeyPress={onKeyPressPrice}
                  onChange={onChangePrice}
                />
              </div>
            </Col>
          </Row>
        </Col>
        <Col xs={12} lg={4} className="w-100">
          <Row className="justify-content-center mt-1">
            <span className="title">Start time</span>
          </Row>
          <Row className="justify-content-center">
            <Col xs={12}>
              <div className="background-input">
                <DatePicker
                  selected={startDateTime}
                  minDate={minimumStartDate}
                  maxDate={maximumStartDate}
                  filterTime={filterTimeStartDate}
                  onChange={(date) => onChangeStartDateTime(date)}
                  showTimeSelect
                  dateFormat={DATE_FORMAT}
                />
              </div>
            </Col>
          </Row>
        </Col>
        <Col xs={12} lg={4} className="w-100">
          <Row className="justify-content-center mt-1">
            <span className="title">Ends time</span>
          </Row>
          <Row className="justify-content-center">
            <Col xs={12}>
              <div className="background-input">
                <DatePicker
                  selected={endDateTime}
                  minDate={minimumEndDate}
                  maxDate={maximumEndDate}
                  filterTime={filterTimeEndDate}
                  onChange={(date) => setEndDateTime(date)}
                  showTimeSelect
                  dateFormat={DATE_FORMAT}
                  popperPlacement="bottom"
                />
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="justify-content-center my-3">
        <Col xs={12}>
          <div className="promote-section-background">
            <Container className="promote-section">
              <Row className="promote-header promote-text justify-content-center text-center">
                <span>By promoting your auction will appear in</span>
              </Row>
              <Row className="promote-header align-items-center justify-content-center">
                <img src={fire} alt="hottest_1" />
                <span className="mx-1">Hottest</span>
                <img src={fire} alt="hottest_2" />
              </Row>
              <Row className="promote-body align-items-center justify-content-center my-4">
                <span className="mr-sm-4 mr-1">Promote this auction</span>
                <ToggleButton
                  className="toggle"
                  checked={isHottest}
                  setChecked={setIsHottest}
                />
              </Row>
              <Row className="promote-footer align-items-center justify-content-center mb-3">
                <span className="text mr-3">Promotion fee</span>
                <span className="value">{promotionFee} GEO$</span>
              </Row>
            </Container>
          </div>
        </Col>
      </Row>
      <div className="text-center message-transactions mb-3">
        There are 2 transactions that require your approval when you are making
        an Auction, you need to confirm "set approval for all" on your metamask,
        then approve contract interaction to start the auction on your wallet.
        Please allow 1 minute while your Auction is being processed.
      </div>
      <Row className="justify-content-center">
        <Col xs={6}>
          <button
            className="confirm-button w-100"
            onClick={() => onClickConfirm()}
            disabled={hasPriceError}
          >
            Confirm
          </button>
        </Col>
      </Row>
    </Container>
  );

  return (
    <>
      <ErrorModal
        message={errorMessage}
        displayModal={displayErrorModal}
        setDisplayModal={setDisplayErrorModal}
      />
      <MessageModal
        displayModal={displayMessageModal}
        setDisplayModal={setDisplayMessageModal}
        title={messageModalTitle}
        message={messageModalBody}
        buttonText={messageModalButtonText}
        buttonHandler={handlerFunction ? redirectToQuickSwap : false}
        footerText={messageModalFooterText}
      />
      <Modal
        size="lg"
        title="Do you want to put this NFT up for auction?"
        bodyContent={getBodyContent()}
        showFooter={false}
        displayModal={displayModal}
        setDisplayModal={setDisplayModal}
      />
    </>
  );
};

export default AuctionSettingsModal;
